import { defineStore } from 'pinia';
import type { IAudioMedia, IImageMedia, IMedia, IVideoMedia, Question } from 'shared-domains';
import MediaModel from '~/models/Media';
import { QuizSettingsModalModes } from '~/editor/EditorTypes';
import type { BrazeInAppMessageType } from '~/components/BrazeInAppMessage/Helper';
import { getBrazeInAppMessageData } from '~/components/BrazeInAppMessage/Helper';
import type { LeadGenModalProps } from '~/components/LeadGenModals/composables/useLeadGenModal';
import type { QfwAIStudioPages } from '~/components/QfwAIStudio/types';
import type { IOrgInfo } from '~/domains/organization';
import type { ResourceType } from '~/types/qfwSharing';
import { $t } from '~/services/i18n';
import $store from '~/services/StoreService';
import eventBus from '~/services/EventBus';
import type { IOutOfStorageModalTriggerParams } from '~/components/MyLibraryV2/Limits/Limits.types';

const messageToCheck = ['version.NOT_ALLOWED'];
const errorCheckCount = 2;
interface MathInsertModalProps {
  onSave: (latex: string) => void,
  onCancel?: (latex: string) => void,
  initialLatex?: string,
}

export enum ImageUploadModalState {
  WaitingForImage,
  GoogleSearchStarted,
  Uploading,
  Cropping,
  Saving,
}

const audioUploadResetState = {
  isShown: false,
  media: MediaModel() as IMedia,

  onSave: (_media: IAudioMedia) => Promise.resolve(),
};

const videoUploadResetState = {
  isShown: false,
  media: MediaModel() as IMedia,

  onSave: (_media: IVideoMedia, _media_source?: string) => Promise.resolve(),
};

const imageUploadResetState = {
  isShown: false,
  media: MediaModel() as IMedia,
  state: ImageUploadModalState.WaitingForImage,
  mode: 'normal' as 'normal' | 'cropping',

  onSave: (_media: IImageMedia) => Promise.resolve(),
};

const mathInsertResetState = {
  latex: '',
  isShown: false,

  onSave: (_latex: string) => {},

  onCancel: (_latex: string) => {},
};

const questionGenModalResetState = {
  isShowing: false,
  questions: [] as Question[],
  filters: {} as Record<string, any>,
};

const mediaPreviewResetState = {
  isShown: false,
  media: MediaModel() as IMedia,
};
const brazeInAppBannerState = {
  isShowing: false,
  inAppBannerData: {} as BrazeInAppMessageType,
};
const leadGenModalState = {
  isShowing: false,
  leadGenModalProps: {} as LeadGenModalProps,
};

const outOfStorageModalState = {
  isShowing: false,
  outOfStorageModalProps: {} as IOutOfStorageModalTriggerParams,
};

const useModalStore = defineStore('modal', {
  state: () => ({
    createNewQuestion: {
      isVisible: false,
      props: {},
    },
    inValidQuestion: {
      isVisible: false,
      callbackAfterDiscard: (() => {}) as () => void,
    },
    imageUpload: {
      ...imageUploadResetState,
    },
    mathInsert: {
      ...mathInsertResetState,
    },
    videoUpload: {
      ...videoUploadResetState,
    },
    audioUpload: {
      ...audioUploadResetState,
    },
    quizSettingsModal: {
      isShown: false,
      mode: QuizSettingsModalModes.SETTINGS,
    },
    publishBlockerModal: {
      isShown: false,
      blockerMessage: '',
    },
    largeModalPreview: {
      isShown: false,
    },
    printWorksheetBottomSettings: {
      isShown: false,
    },
    curriculumSelectionModal: {
      isShown: false,
    },
    questionTopicTaggerModal: {
      isShown: false,
      questionId: '',
    },
    mediaPreview: {
      ...mediaPreviewResetState,
    },
    questionGenModal: {
      ...questionGenModalResetState,
    },
    editorIaOnboardingModal: {
      isShown: false,
    },
    brazeInAppBannerState: {
      ...brazeInAppBannerState,
    },
    quickEditQuestionModal: {
      isShown: false,
      question: {} as Question,
      quizId: '',
      onSave: () => {},
      onCancel: (() => {}) as () => void,
    },
    audioAnswerExplanationPopup: {
      show: false,
    },
    importFormsModal: {
      show: false,
    },
    blockImportModal: {
      show: false,
    },
    updateQuestionErrorModal: {
      isShown: false,
      countHolder: {} as Record<string, number>,
    },
    leadGenModalState: {
      ...leadGenModalState,
    },
    embedQuizModal: {
      show: false,
    },
    memesetAddOrEditModal: {
      show: false,
      isEditModal: false,
      modalTitle: $t('Create meme set'),
      id: '',
      name: '',
      lang: 'English',
      isPublic: false,
    },
    mobileSidebar: {
      show: false,
    },
    outOfStorageModalState: {
      ...outOfStorageModalState,
    },
    courseComplianceSettingsModal: {
      show: false,
    },
    addToCourseModal: {
      show: false,
      quizId: '',
    },
    qfwShareModal: {
      show: false,
      resourceId: '',
      resourceType: '' as ResourceType,
    },
    qfwImportTemplate: {
      show: false,
    },
    qfwBulkImport: {
      show: false,
    },
    qfwAIStudio: {
      show: false,
      activeTab: '' as QfwAIStudioPages,
      source: '',
      quizId: '',
      versionId: '',
    },
    qfwInviteModal: {
      show: false,
      source: '',
      totalAvailableSeats: 0,
      orgInfo: {} as IOrgInfo,
    },
    qfwAIQuizGenModal: {
      show: false,
      prompt: '',
      source: '',
      searchTerm: '',
      useSearchMode: false,
      quizId: '',
      versionId: '',
    },
  }),
  getters: {
    isImageUploadModalShown(state): boolean {
      return state.imageUpload.isShown;
    },

    imageUploadModalState(state) {
      return state.imageUpload;
    },

    editorIaOnboardingModalState(state) {
      return state.editorIaOnboardingModal;
    },

    getImageUploadModalState(state): ImageUploadModalState {
      return state.imageUpload.state;
    },

    getImageUploadUrl(state): string {
      return state.imageUpload.media.url ?? '';
    },

    getSaveFunction(state): (media: IImageMedia) => Promise<void> {
      return state.imageUpload.onSave;
    },

    isMathInsertModalShown(state): boolean {
      return state.mathInsert.isShown;
    },

    getMathInsertSaveFunction(state): (latex: string) => void {
      return state.mathInsert.onSave;
    },

    getMathInsertCancelFunction(state): (latex: string) => void {
      return state.mathInsert.onCancel;
    },

    isAudioUploadModalShown(state): boolean {
      return state.audioUpload.isShown;
    },

    isVideoUploadModalShown(state): boolean {
      return state.videoUpload.isShown;
    },

    publishQuizModalState(state) {
      return state.quizSettingsModal;
    },

    publishBlockerModalState(state) {
      return state.publishBlockerModal;
    },

    curriculumSelectionModalState(state) {
      return state.curriculumSelectionModal;
    },

    questionTopicTaggerModalState(state) {
      return state.questionTopicTaggerModal;
    },

    isMediaPreviewModalShown(state): boolean {
      return state.mediaPreview.isShown;
    },

    getMediaPreviewModalMedia(state): IMedia {
      return state.mediaPreview.media;
    },

    questionGenModalState(state) {
      return state.questionGenModal;
    },

    isQuickEditQuestionModalShown(state): boolean {
      return state.quickEditQuestionModal.isShown;
    },

    quickEditQuestionState(state) {
      return state.quickEditQuestionModal;
    },

    isQuestionErrorModalShown(state): boolean {
      return state.updateQuestionErrorModal.isShown;
    },

    anyModalCurrentlyVisible(state): boolean {
      let result = false;

      Object.keys(state).forEach((key) => {
        if (!key || typeof key !== 'string') { return; }
        // @ts-expect-error key of any type
        if (state[key] && state[key]?.isShown) {
          result = true;
        }
      });
      return result;
    },
    getMobileSidebarState(state) {
      return state.mobileSidebar;
    },
    getBrazeInAppBannerState(state) {
      return state.brazeInAppBannerState;
    },
    getLeadGenModalState(state) {
      return state.leadGenModalState;
    },
    getEmbedQuizModalState(state) {
      return state.embedQuizModal;
    },
    getMemesetAddOrEditModalState(state) {
      return state.memesetAddOrEditModal;
    },
    getOutOfStorageModalState(state) {
      return state.outOfStorageModalState;
    },
    getCourseComplianceSettingsModalState(state) {
      return state.courseComplianceSettingsModal;
    },
    getQfwShareModalState(state) {
      return state.qfwShareModal;
    },
    getAddToCourseModalState(state) {
      return state.addToCourseModal;
    },
    getQfwImportTemplateState(state) {
      return state.qfwImportTemplate;
    },
    getQfwBulkImportState(state) {
      return state.qfwBulkImport;
    },
    getQfwAIStudioState(state) {
      return state.qfwAIStudio;
    },
    getQfwInviteModalState(state) {
      return state.qfwInviteModal;
    },
    getQfwAIQuizGenModalState(state) {
      return state.qfwAIQuizGenModal;
    },
  },
  actions: {
    toggleCreateNewQuestionModal({ isVisible, props = {} }: { isVisible: boolean; props?: any }) {
      this.createNewQuestion.isVisible = isVisible;
      this.createNewQuestion.props = props;
    },

    toggleInvalidQuestionModal({ isVisible, callbackAfterDiscard = () => {} }: { isVisible: boolean; callbackAfterDiscard?: () => void }) {
      this.inValidQuestion.isVisible = isVisible;
      this.inValidQuestion.callbackAfterDiscard = callbackAfterDiscard;
    },

    toggleEditorIaOnboardingModal({ isShown }: { isShown: boolean }) {
      this.editorIaOnboardingModal.isShown = isShown;
    },

    hideImageUploadModal() {
      this.imageUpload.isShown = false;
    },

    showImageUploadModal(onSave: (media: IMedia) => Promise<void>, media?: IMedia, mode?: 'normal' | 'cropping') {
      if (media) {
        this.imageUpload.media = media;
      } else {
        this.imageUpload.media = { ...imageUploadResetState.media };
      }
      this.imageUpload.mode = mode || 'normal';
      this.imageUpload.onSave = onSave;
      this.imageUpload.isShown = true;
    },

    setImageUploadModalState(state: ImageUploadModalState) {
      if (this.imageUpload.state === state)
      { return; }
      this.imageUpload.state = state;
    },

    setImageUploadUrl(url: string) {
      if (this.imageUpload.media.url === url)
      { return; }
      this.imageUpload.media.url = url;
    },

    resetImageUploadModalState() {
      this.imageUpload = {
        ...imageUploadResetState,
      };
    },

    hideMathInsertModal() {
      this.mathInsert.isShown = false;
    },

    showMathInsertModal({ onSave, onCancel, initialLatex }: MathInsertModalProps) {
      if (initialLatex) {
        this.mathInsert.latex = initialLatex;
      }
      this.mathInsert.onSave = onSave;
      if (onCancel && typeof onCancel === 'function') {
        this.mathInsert.onCancel = onCancel;
      }
      this.mathInsert.isShown = true;
    },

    setMathInsertLatex(latex: string) {
      this.mathInsert.latex = latex;
    },

    resetMathInsertModalState() {
      this.mathInsert = {
        ...mathInsertResetState,
      };
    },

    hideVideoUploadModal() {
      this.videoUpload.isShown = false;
    },

    showVideoUploadModal(onSave: (media: IMedia, mediaSource?: string) => Promise<void>, media?: IMedia) {
      if (media) {
        this.videoUpload.media = media;
      } else {
        this.videoUpload.media = { ...videoUploadResetState.media };
      }
      this.videoUpload.onSave = onSave;
      this.videoUpload.isShown = true;
    },

    hideAudioUploadModal() {
      this.audioUpload.isShown = false;
    },

    showAudioUploadModal(onSave: (media: IMedia) => Promise<void>, media?: IMedia) {
      if (media) {
        this.audioUpload.media = media;
      } else {
        this.audioUpload.media = { ...audioUploadResetState.media };
      }
      this.audioUpload.onSave = onSave;
      this.audioUpload.isShown = true;
    },

    showQuizSettingsModal(mode: QuizSettingsModalModes = QuizSettingsModalModes.SETTINGS) {
      this.quizSettingsModal.isShown = true;
      this.quizSettingsModal.mode = mode;
    },

    hideQuizSettingsModal() { this.quizSettingsModal.isShown = false; },

    showPublishBlockerModal(blockerMessage: string = '') {
      this.publishBlockerModal.isShown = true;
      this.publishBlockerModal.blockerMessage = blockerMessage;
    },

    hidePublishBlockerModal() {
      this.publishBlockerModal.isShown = false;
      this.publishBlockerModal.blockerMessage = '';
    },

    toggleCurriculumSelectionModal({ isShown }: { isShown: boolean }) {
      this.curriculumSelectionModal.isShown = isShown;
    },

    toggleQuestionTopicTaggerModal({ isShown, questionId = '' }: { isShown: boolean; questionId?: string }) {
      this.questionTopicTaggerModal.isShown = isShown;
      this.questionTopicTaggerModal.questionId = questionId;
    },

    toggleLargeModalPreview({ isShown }: { isShown: boolean }) {
      this.largeModalPreview.isShown = isShown;
    },

    togglePrintWorksheetBottomSettings({ isShown }: { isShown: boolean }) {
      this.printWorksheetBottomSettings.isShown = isShown;
    },

    showMediaPreviewModal(media: IMedia) {
      this.mediaPreview.media = media;
      this.mediaPreview.isShown = true;
    },

    hideMediaPreviewModal() {
      this.mediaPreview.media = { ...mediaPreviewResetState.media };
      this.mediaPreview.isShown = false;
    },

    setQuestionModalState({ filters, questions }: { filters: Record<string, any>; questions: Question[] }) {
      this.questionGenModal.filters = filters;
      this.questionGenModal.questions = questions;
    },

    showQuickEditQuestionModal() {
      this.quickEditQuestionModal.isShown = true;
    },

    hideQuickEditQuestionModal() {
      this.quickEditQuestionModal.isShown = false;
    },

    setQuickEditQuestionModalState(
      { question, quizId, onSave, onCancel }:
      { question: Question; quizId: string; onSave: () => void; onCancel: () => void }) {
      this.quickEditQuestionModal.question = question;
      this.quickEditQuestionModal.quizId = quizId;
      this.quickEditQuestionModal.onSave = onSave;
      this.quickEditQuestionModal.onCancel = onCancel;
    },
    toggleAudioAnswerExplanationPopup(value: boolean) {
      this.audioAnswerExplanationPopup.show = value ?? !this.audioAnswerExplanationPopup.show;
    },

    setImportFormsModalState(value: boolean) {
      this.importFormsModal.show = value;
    },

    setBlockImportModalState(value: boolean) {
      this.blockImportModal.show = value;
    },

    resetQuestionErrorModalState() {
      this.updateQuestionErrorModal.isShown = false;
      this.updateQuestionErrorModal.countHolder = {};
    },

    recordQuestionUpdateError(message: string) {
      this.updateQuestionErrorModal.countHolder[message] = (this.updateQuestionErrorModal.countHolder[message] || 0) + 1;
      if (this.updateQuestionErrorModal.countHolder[message] >= errorCheckCount && messageToCheck.includes(message)) {
        this.updateQuestionErrorModal.isShown = true;
      }
    },
    showBrazeInAppBanner(InAppBannerData: any) {
      this.brazeInAppBannerState.inAppBannerData = getBrazeInAppMessageData(InAppBannerData);
      this.brazeInAppBannerState.isShowing = true;
    },
    hideBrazeInAppBanner() {
      this.brazeInAppBannerState.inAppBannerData = {} as BrazeInAppMessageType;
      this.brazeInAppBannerState.isShowing = false;
    },
    showLeadGenModal(leadGenModalProps: LeadGenModalProps) {
      this.leadGenModalState.isShowing = true;
      this.leadGenModalState.leadGenModalProps = leadGenModalProps;
    },
    hideAdminLeadGenModal() {
      this.leadGenModalState.isShowing = false;
      this.leadGenModalState.leadGenModalProps = {} as LeadGenModalProps;
    },
    showOutOfStorageModal(outOfStorageModalProps: IOutOfStorageModalTriggerParams) {
      this.outOfStorageModalState.isShowing = true;
      this.outOfStorageModalState.outOfStorageModalProps = outOfStorageModalProps;
    },
    hideOutOfStorageModal() {
      this.outOfStorageModalState.isShowing = false;
      this.outOfStorageModalState.outOfStorageModalProps = {} as IOutOfStorageModalTriggerParams;
    },
    toggleEmbedQuizModal({ show }: { show: boolean }) {
      this.embedQuizModal.show = show;
    },
    toggleMemesetAddOrEditModal(args: {
      show: boolean,
      isEditModal?: boolean,
      id?: string,
      name?: string,
      lang?: string,
      isPublic?: boolean,
    }) {
      const { show, isEditModal = false, id = '', name = '', lang = 'English', isPublic = false } = args;
      this.memesetAddOrEditModal.show = show;
      this.memesetAddOrEditModal.isEditModal = isEditModal;
      this.memesetAddOrEditModal.modalTitle = isEditModal ? $t('Edit meme set') : $t('Create meme set');
      this.memesetAddOrEditModal.id = id;
      this.memesetAddOrEditModal.name = name;
      this.memesetAddOrEditModal.lang = lang;
      this.memesetAddOrEditModal.isPublic = isPublic;
    },
    toggleMobileSidebar({ show }: { show: boolean }) {
      this.mobileSidebar.show = show;
      if (show) {
        eventBus.$emit('overlay:show', { zIndex: 102 });
        $store().dispatch('root/showHelpButton');
      } else {
        eventBus.$emit('overlay:hide');
        $store().dispatch('root/hideHelpButton');
      }
    },
    setCourseComplianceSettingsModalState(show: boolean) {
      this.courseComplianceSettingsModal.show = show;
    },
    setQfwShareModalState({ show, resourceId, resourceType }: { show: boolean; resourceId: string; resourceType: ResourceType }) {
      this.qfwShareModal.show = show;
      this.qfwShareModal.resourceId = resourceId;
      this.qfwShareModal.resourceType = resourceType;
    },
    resetQfwShareModalState() {
      this.qfwShareModal.show = false;
      this.qfwShareModal.resourceId = '';
      this.qfwShareModal.resourceType = '' as ResourceType;
    },
    setAddToCourseModalState({ show, quizId }: { show: boolean; quizId: string }) {
      this.addToCourseModal.show = show;
      this.addToCourseModal.quizId = quizId;
    },
    toggleQfwImportTemplateModal({ show }: { show: boolean }) {
      this.qfwImportTemplate.show = show;
    },
    toggleQfwBulkImportModal({ show }: { show: boolean }) {
      this.qfwBulkImport.show = show;
    },
    toggleQfwAIStudioModal(args: { show: boolean; activeTab?: string; source?: string; quizId?: string; versionId?: string }) {
      const { show, activeTab = '', source = '', quizId = '', versionId = '' } = args;
      this.qfwAIStudio.show = show;
      this.qfwAIStudio.activeTab = activeTab as QfwAIStudioPages;
      this.qfwAIStudio.source = source;
      this.qfwAIStudio.quizId = quizId;
      this.qfwAIStudio.versionId = versionId;
    },
    toggleQfwInviteModal(args: { show: boolean; source?: string; totalAvailableSeats?: number; orgInfo?: IOrgInfo }) {
      const { show, source = '', totalAvailableSeats = 0, orgInfo = {} } = args;
      this.qfwInviteModal.show = show;
      this.qfwInviteModal.source = source;
      this.qfwInviteModal.totalAvailableSeats = totalAvailableSeats;
      this.qfwInviteModal.orgInfo = orgInfo as IOrgInfo;
    },
    toggleQfwAIQuizGenModal(args: {
      show: boolean,
      prompt?: string,
      source?: string,
      searchTerm?: string,
      useSearchMode?: boolean,
      quizId?: string,
      versionId?: string,
    }) {
      const { show, prompt = '', source = '', searchTerm = '', useSearchMode = false, quizId = '', versionId = '' } = args;
      this.qfwAIQuizGenModal.show = show;
      this.qfwAIQuizGenModal.prompt = prompt;
      this.qfwAIQuizGenModal.source = source;
      this.qfwAIQuizGenModal.searchTerm = searchTerm;
      this.qfwAIQuizGenModal.useSearchMode = useSearchMode;
      this.qfwAIQuizGenModal.quizId = quizId;
      this.qfwAIQuizGenModal.versionId = versionId;
    },
  },
});

export default useModalStore;
